import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  TextField, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  Chip 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ExpandMore as ExpandMoreIcon, 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon 
} from '@mui/icons-material';
import GradientText from '../common/GradientText.tsx';
import { SurrogateRole } from '../../data/surrogateRoles.ts';

interface SurrogateRoleInfoProps {
  roleKey: string;
  roleInfo: SurrogateRole;
  onInfoChange: (roleKey: string, updatedInfo: SurrogateRole) => void;
}

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1)
}));

const EditableContentBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.3)',
  }
}));

const EditButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  background: 'rgba(26, 26, 46, 0.7)',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.2)',
  }
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  background: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    background: 'rgba(0, 204, 153, 0.2)',
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  color: '#fff',
  borderRadius: '8px !important',
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
    marginBottom: theme.spacing(2),
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
  '&.Mui-expanded': {
    minHeight: 48,
  }
}));

const SurrogateRoleInfo: React.FC<SurrogateRoleInfoProps> = ({ 
  roleKey, 
  roleInfo, 
  onInfoChange 
}) => {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [tempEditContent, setTempEditContent] = useState('');
  
  // Handle edit button click
  const handleEditClick = (section: string, content: string) => {
    setEditingSection(section);
    setTempEditContent(content);
  };
  
  // Handle save edit
  const handleSaveEdit = (section: string) => {
    if (!onInfoChange) return;
    
    const updatedInfo = { ...roleInfo };
    
    if (section === 'background') {
      updatedInfo.background = tempEditContent;
    } else if (section === 'context') {
      updatedInfo.context = tempEditContent;
    } else if (section === 'objective') {
      updatedInfo.objective = tempEditContent;
    } else if (section === 'expectedOutput') {
      updatedInfo.expectedOutput = tempEditContent;
    }
    
    onInfoChange(roleKey, updatedInfo);
    setEditingSection(null);
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingSection(null);
  };
  
  // Render editable content section
  const renderEditableContent = (section: string, title: string, content: string) => {
    return (
      <EditableContentBox>
        <SectionTitle variant="h6">{title}</SectionTitle>
        
        {editingSection === section ? (
          <>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={tempEditContent}
              onChange={(e) => setTempEditContent(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(138, 43, 226, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8A2BE2',
                  },
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <IconButton 
                size="small" 
                onClick={() => handleSaveEdit(section)}
                sx={{ color: '#00CC99', mr: 1 }}
              >
                <SaveIcon />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={handleCancelEdit}
                sx={{ color: '#FF6F00' }}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {content}
            </Typography>
            <EditButton size="small" onClick={() => handleEditClick(section, content)}>
              <EditIcon fontSize="small" sx={{ color: '#00CC99' }} />
            </EditButton>
          </>
        )}
      </EditableContentBox>
    );
  };
  
  return (
    <Box sx={{ mt: 4 }}>
      <GradientText gradient="primary" variant="h5" sx={{ mb: 2 }}>
        {roleInfo.title} Role Information
      </GradientText>
      
      <StyledAccordion defaultExpanded={false}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00CC99' }} />}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Background</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          {renderEditableContent('background', 'Professional Background', roleInfo.background)}
        </AccordionDetails>
      </StyledAccordion>
      
      <StyledAccordion defaultExpanded={false}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00CC99' }} />}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Key Principles</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          <List sx={{ p: 0 }}>
            {roleInfo.keyPrinciples.map((principle, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemButton 
                  sx={{ 
                    borderRadius: 1,
                    background: 'rgba(26, 26, 46, 0.7)',
                    mb: 1,
                    '&:hover': {
                      background: 'rgba(138, 43, 226, 0.1)',
                    }
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#00CC99', fontWeight: 600 }}>
                      {principle.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {principle.description}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </StyledAccordion>
      
      <StyledAccordion defaultExpanded={false}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00CC99' }} />}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Architecture</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 3 }}>
            <SectionTitle variant="subtitle2">Core Skills & Domain Expertise</SectionTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              {roleInfo.architecture.coreSkills.map((skill, index) => (
                <SkillChip key={index} label={skill} />
              ))}
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <SectionTitle variant="subtitle2">Knowledge & Abilities</SectionTitle>
            <List dense sx={{ pl: 2 }}>
              {roleInfo.architecture.knowledge.map((item, index) => (
                <ListItem key={index} sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {item}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
          
          <Box>
            <SectionTitle variant="subtitle2">Frameworks & Best Practices</SectionTitle>
            <List dense sx={{ pl: 2 }}>
              {roleInfo.architecture.frameworks.map((item, index) => (
                <ListItem key={index} sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {item}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </AccordionDetails>
      </StyledAccordion>
      
      <StyledAccordion defaultExpanded={false}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#00CC99' }} />}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Project Details</Typography>
        </StyledAccordionSummary>
        <AccordionDetails>
          {renderEditableContent('context', 'Context', roleInfo.context)}
          {renderEditableContent('objective', 'Objective', roleInfo.objective)}
          {renderEditableContent('expectedOutput', 'Expected Output', roleInfo.expectedOutput)}
        </AccordionDetails>
      </StyledAccordion>
    </Box>
  );
};

export default SurrogateRoleInfo; 